import Chapter from "@/components/Chapter";
import Reveal from "@/components/Reveal";
import { FABRICS } from "@/data/content";

const FabricCard = ({ fabric, index }) => (
  <Reveal delay={(index % 4) * 0.08} className="h-full">
    <article
      data-testid={`fabric-card-${fabric.id}`}
      className="group flex h-full flex-col border border-navy/10 bg-white transition-colors duration-500 hover:border-gold/60"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-navy">
        <img
          data-testid={`fabric-image-${fabric.id}`}
          src={fabric.image}
          alt={`${fabric.name} fabric texture`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
          onError={(e) => (e.currentTarget.style.opacity = 0)}
        />
        <div className="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 bg-gold transition-transform duration-500 group-hover:scale-x-100" />
      </div>
      <div className="p-6">
        <h3 className="font-serif text-xl font-medium text-navy sm:text-2xl">{fabric.name}</h3>
        <div className="mt-3 flex flex-wrap gap-2">
          {fabric.loom.map((l) => (
            <span
              key={l}
              data-testid={`fabric-loom-${fabric.id}-${l.toLowerCase().replace(" ", "-")}`}
              className="border border-royal/25 bg-royal/5 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-royal"
            >
              {l}
            </span>
          ))}
        </div>
        <p className="mt-3 min-h-[3.5rem] text-sm leading-relaxed text-slate-600">{fabric.tagline}</p>
        <dl className="mt-5 space-y-2 border-t border-navy/10 pt-4 text-xs">
          <div className="flex justify-between gap-4">
            <dt className="uppercase tracking-[0.15em] text-slate-400">Composition</dt>
            <dd className="text-right font-medium text-navy">{fabric.composition}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="uppercase tracking-[0.15em] text-slate-400">Weight</dt>
            <dd className="text-right font-medium text-navy">{fabric.weight}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="uppercase tracking-[0.15em] text-slate-400">Weave</dt>
            <dd className="text-right font-medium text-navy">{fabric.weave}</dd>
          </div>
          <div className="flex justify-between gap-4">
            <dt className="uppercase tracking-[0.15em] text-slate-400">Loom</dt>
            <dd className="text-right font-medium text-navy">{fabric.loom.join(" or ")}</dd>
          </div>
        </dl>
        {fabric.note && (
          <p
            data-testid={`fabric-note-${fabric.id}`}
            className="mt-5 border-l-2 border-gold bg-cream/70 p-4 text-xs leading-relaxed text-slate-600"
          >
            <span className="mb-1 block text-[9px] font-semibold uppercase tracking-[0.2em] text-royal">
              Sustainability
            </span>
            {fabric.note}
          </p>
        )}
      </div>
    </article>
  </Reveal>
);

const FeatureCard = ({ fabric }) => (
  <Reveal delay={0.08}>
    <article
      data-testid={`fabric-card-${fabric.id}`}
      className="group grid overflow-hidden border border-navy/10 bg-white transition-colors duration-500 hover:border-gold/60 lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-navy lg:aspect-auto lg:min-h-[340px]">
        <img
          data-testid={`fabric-image-${fabric.id}`}
          src={fabric.image}
          alt={`${fabric.name} fabric texture`}
          loading="lazy"
          className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
        />
      </div>
      <div className="p-8 sm:p-10">
        <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-royal">Newest family · since 2015</p>
        <h3 className="mt-3 font-serif text-3xl font-medium text-navy">{fabric.name}</h3>
        <div className="mt-4 flex flex-wrap gap-2">
          {fabric.loom.map((l) => (
            <span
              key={l}
              data-testid={`fabric-loom-${fabric.id}-${l.toLowerCase().replace(" ", "-")}`}
              className="border border-royal/25 bg-royal/5 px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.18em] text-royal"
            >
              {l}
            </span>
          ))}
        </div>
        <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600">{fabric.tagline}</p>
        <dl className="mt-6 grid gap-x-10 gap-y-3 border-t border-navy/10 pt-5 text-xs sm:grid-cols-2">
          {[
            ["Composition", fabric.composition],
            ["Weight", fabric.weight],
            ["Weave", fabric.weave],
            ["Loom", fabric.loom.join(" or ")],
          ].map(([k, v]) => (
            <div key={k} className="flex justify-between gap-4">
              <dt className="uppercase tracking-[0.15em] text-slate-400">{k}</dt>
              <dd className="text-right font-medium text-navy">{v}</dd>
            </div>
          ))}
        </dl>
        <p
          data-testid={`fabric-note-${fabric.id}`}
          className="mt-6 border-l-2 border-gold bg-cream/70 p-5 text-xs leading-relaxed text-slate-600"
        >
          <span className="mb-1 block text-[9px] font-semibold uppercase tracking-[0.2em] text-royal">
            Sustainability
          </span>
          {fabric.note}
        </p>
      </div>
    </article>
  </Reveal>
);

const Fabrics = () => (
  <section id="fabrics" data-testid="fabric-range-section" className="bg-cream py-24 md:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Chapter
        index="03"
        eyebrow="The Fabric Range"
        title="Multiple varieties of fabrics, milled to your specification."
        description="Every fabric below is woven to order — composition, weight, width and finish engineered around your end use, on handloom or power loom as your programme demands. Nothing is pulled from a shelf."
      />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {FABRICS.filter((f) => !f.note).map((f, i) => (
          <FabricCard key={f.id} fabric={f} index={i} />
        ))}
      </div>
      <div className="mt-6">
        {FABRICS.filter((f) => f.note).map((f) => (
          <FeatureCard key={f.id} fabric={f} />
        ))}
      </div>
      <Reveal delay={0.1} className="mt-12 text-center">
        <p className="font-serif text-lg italic text-slate-600">
          Custom compositions, weights and finishes on every family — yarn dye, piece dye, clipping and calendering in-house.
        </p>
      </Reveal>
    </div>
  </section>
);

export default Fabrics;
